import { WORDS } from '../constants/wordlist'
import { VALID_GUESSES } from '../constants/validGuesses'
import { WRONG_SPOT_MESSAGE, NOT_CONTAINED_MESSAGE } from '../constants/strings'
import { getGuessStatuses } from './statuses'

export const isWordInWordList = (word: string) => {
  return (
    WORDS.includes(word.toLowerCase()) ||
    VALID_GUESSES.includes(word.toLowerCase())
  )
}

export const getOrdinalNumberInArabic = (n: number): string => {
  switch (n) {
    case 1:
      return 'الأول'
    case 2:
      return 'الثاني'
    case 3:
      return 'الثالث'
    case 4:
      return 'الرابع'
    case 5:
      return 'الخامس'
    default:
      return ''
  }
}
export const getNameOfLetterInArabic = (l: string): string => {
  switch (l) {
    case 'ا':
      return 'الألف'
    case 'أ':
      return 'الألف مع همزة'
    case 'إ':
      return 'الألف مع همزة'
    case 'آ':
      return 'الألف الممدودة'
    case 'ب':
      return 'الباء'
    case 'ت':
      return 'التاء'
    case 'ث':
      return 'الثاء'
    case 'ج':
      return 'الجيم'
    case 'ح':
      return 'الحاء'
    case 'خ':
      return 'الخاء'
    case 'د':
      return 'الدال'
    case 'ذ':
      return 'الذال'
    case 'ر':
      return 'الراء'
    case 'ز':
      return 'الزاي'
    case 'س':
      return 'السين'
    case 'ش':
      return 'الشين'
    case 'ص':
      return 'الصاد'
    case 'ض':
      return 'الضاد'
    case 'ط':
      return 'الطاء'
    case 'ظ':
      return 'الظاء'
    case 'ع':
      return 'العين'
    case 'غ':
      return 'الغين'
    case 'ف':
      return 'الفاء'
    case 'ق':
      return 'القاف'
    case 'ك':
      return 'الكاف'
    case 'ل':
      return 'اللام'
    case 'م':
      return 'الميم'
    case 'ن':
      return 'النون'
    case 'ه':
      return 'الهاء'
    case 'و':
      return 'الواو'
    case 'ي':
      return 'الياء'
    case 'ى':
      return 'الألف المقصورة'
    case 'ء':
      return 'الهمزة'
    case 'ؤ':
      return 'الهمزة على واو'
    case 'ئ':
      return 'الهمزة على نبرة'
    case 'ة':
      return 'التاء المربوطة'
    default:
      return ''
  }
}
export const OkJudgment: Judgment = {
  getType: () => 'ok',
  getLetter: () => '',
  getPosition: () => -1,
}

export interface Judgment {
  getType(): 'ok' | 'missing_present' | 'missing_correct'
  getLetter(): string
  getPosition(): number
}

export const isValidInHardMode = (
  word: string,
  guesses: string[]
): Judgment => {
  const allIndices = (str: string, char: string) => {
    var indices = []
    for (var i = 0; i < str.length; i++) {
      if (str[i] === char[0]) indices.push(i)
    }
    return indices
  }

  const lastGuess = guesses.at(-1)
  var judgments: Judgment[] = []
  switch (lastGuess) {
    case undefined:
      break
    default:
      const statuses = getGuessStatuses(lastGuess)
      lastGuess.split('').forEach((letter, index) => {
        switch (statuses[index]) {
          case 'absent':
            break
          case 'present':
            if (!word.includes(letter)) {
              judgments.push({
                getType: () => 'missing_present',
                getLetter: () => letter,
                getPosition: () => -1,
              })
            }
            break
          case 'correct':
            if (!allIndices(word, letter).includes(index)) {
              judgments.push({
                getType: () => 'missing_correct',
                getLetter: () => letter,
                getPosition: () => index,
              })
            }
        }
      })
  }

  return judgments.reverse().reduce((prev, current) => {
    if (
      prev.getType() === 'missing_correct' &&
      current.getType() === 'missing_present'
    ) {
      return prev
    } else {
      return current
    }
  }, OkJudgment)
}

export const isWinningWord = (word: string) => {
  return solution === word
}

// build a set of previously revealed letters - present and correct
// guess must use correct letters in that space and any other revealed letters
// also check if all revealed instances of a letter are used (i.e. two C's)
export const findFirstUnusedReveal = (word: string, guesses: string[]) => {
  if (guesses.length === 0) {
    return false
  }

  const lettersLeftArray = new Array<string>()
  const guess = guesses[guesses.length - 1]
  const statuses = getGuessStatuses(guess)

  for (let i = 0; i < guess.length; i++) {
    if (statuses[i] === 'correct' || statuses[i] === 'present') {
      lettersLeftArray.push(guess[i])
    }
    if (statuses[i] === 'correct' && word[i] !== guess[i]) {
      return WRONG_SPOT_MESSAGE(guess[i], i + 1)
    }
  }

  // check for the first unused letter, taking duplicate letters
  // into account - see issue #198
  let n
  for (const letter of word) {
    n = lettersLeftArray.indexOf(letter)
    if (n !== -1) {
      lettersLeftArray.splice(n, 1)
    }
  }

  if (lettersLeftArray.length > 0) {
    return NOT_CONTAINED_MESSAGE(lettersLeftArray[0])
  }
  return false
}

export const getWordOfDay = () => {
  // January 26, 2022 Game Epoch
  const epochMs = 1643148000000
  const now = Date.now()
  const msInDay = 86400000
  const index = Math.floor((now - epochMs) / msInDay)
  const nextday = (index + 1) * msInDay + epochMs

  return {
    solution: WORDS[index % WORDS.length].toUpperCase(),
    solutionIndex: index,
    tomorrow: nextday,
  }
}

export const { solution, solutionIndex, tomorrow } = getWordOfDay()
